import React from 'react';
import { Card } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

type TimeRange = 'week' | 'month';

interface CompetitorComparisonCardProps {
  username: string;
  followers: number;
  postsCount: number;
  postsPerWeek: number | null;
  growth: number | null;
  followersChange: number | null;
  isCurrentUser?: boolean;
  timeRange?: TimeRange;
}

const CompetitorComparisonCard = ({ 
  username, 
  followers,
  postsCount,
  postsPerWeek, 
  growth,
  followersChange,
  isCurrentUser = false,
  timeRange = 'week'
}: CompetitorComparisonCardProps) => {
  const { t } = useLanguage();
  const isMonthly = timeRange === 'month';
  
  const formatFollowers = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatFollowersChange = (num: number | null): string => {
    if (num === null) return '—';
    const sign = num > 0 ? '+' : '';
    if (Math.abs(num) >= 1000000) return `${sign}${(num / 1000000).toFixed(1)}M`;
    if (Math.abs(num) >= 1000) return `${sign}${(num / 1000).toFixed(1)}K`;
    return `${sign}${num}`;
  };

  const getPostsPerWeekColor = (value: number | null) => {
    if (value === null) return 'bg-gray-400';
    if (value >= 3) return 'bg-green-500';
    if (value >= 2) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getPostsPerWeekWidth = (value: number | null) => {
    if (value === null) return 0;
    const maxTarget = isMonthly ? 12 : 3; // 12 posts/month or 3 posts/week
    return Math.min((value / maxTarget) * 100, 100);
  };

  const getGrowthBarColor = (value: number | null) => {
    if (value === null) return 'bg-gray-400';
    if (value > 0) return 'bg-green-500';
    if (value < 0) return 'bg-red-500';
    return 'bg-gray-400';
  };

  const getGrowthBarWidth = (value: number | null) => {
    if (value === null) return 0;
    // Scale: max 1% growth as full bar
    const normalized = Math.max(-1, Math.min(1, Math.abs(value)));
    return (normalized / 1) * 50;
  };

  return (
    <div className={cn(
      "glass rounded-xl p-4 transition-all border-2 border-border hover:border-primary/40 hover:shadow-md group relative overflow-hidden",
      isCurrentUser && "border-primary/60"
    )}>
      {isCurrentUser && (
        <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
      )}
      
      <div className="relative space-y-3">
        {/* Username Header */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-foreground truncate">@{username}</h3>
          </div>
          {isCurrentUser && (
            <span className="text-[10px] font-semibold text-primary px-2 py-0.5 bg-primary/10 rounded-full">
              {t('competitors.you')}
            </span>
          )}
        </div>

        <div className="space-y-2.5">
          {/* Posts per Period */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground w-[80px] flex-shrink-0 whitespace-nowrap">
              Liczba postów
            </span>
            <div className="flex items-center gap-2">
              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden border border-border">
                <div 
                  className={cn("h-full transition-all duration-500 rounded-full", getPostsPerWeekColor(postsPerWeek))}
                  style={{ width: `${getPostsPerWeekWidth(postsPerWeek)}%` }}
                />
              </div>
              <span className="text-xs font-bold text-foreground w-12 text-right tabular-nums">
                {postsPerWeek !== null ? postsPerWeek.toFixed(1) : '—'}
              </span>
            </div>
          </div>

          {/* Growth */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground w-[72px] flex-shrink-0">{t('competitors.growth')}</span>
            <div className="flex items-center gap-2">
              <div className="relative w-16 h-1.5 bg-muted rounded-full overflow-hidden border border-border">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
                {growth !== null && growth !== 0 && (
                  <div 
                    className={cn("absolute top-0 bottom-0 h-full transition-all duration-500 rounded-full", getGrowthBarColor(growth))}
                    style={{
                      width: `${getGrowthBarWidth(growth)}%`,
                      left: growth > 0 ? '50%' : `${50 - getGrowthBarWidth(growth)}%`
                    }}
                  />
                )}
              </div>
              <span className={cn(
                "text-xs font-bold w-12 text-right tabular-nums",
                growth !== null && growth > 0 && "text-green-500",
                growth !== null && growth < 0 && "text-red-500",
                (growth === null || growth === 0) && "text-muted-foreground"
              )}>
                {growth !== null ? `${growth > 0 ? '+' : ''}${growth.toFixed(2)}%` : '—'}
              </span>
            </div>
          </div>

          {/* Followers Change */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground w-[72px] flex-shrink-0">Obserwatorzy</span>
            <span className={cn(
              "text-xs font-bold tabular-nums",
              followersChange !== null && followersChange > 0 && "text-green-500",
              followersChange !== null && followersChange < 0 && "text-red-500",
              (followersChange === null || followersChange === 0) && "text-muted-foreground"
            )}>
              {formatFollowersChange(followersChange)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorComparisonCard;
